<template>
  <modal
    ref="modal"
    modal-title="Upload participants"
  >
    <template #modal-content>
      <div
        v-if="welcomeVisible"
        class="upload-container"
      >
        <div
          v-if="uploadButtonVisible"
          class="upload-row"
        >
          <div class="upload-col tight button">
            <label class="btn btn-sm btn-primary">
              <span class="glyphicons icon-left upload align-middle" /><span class="align-middle">Select file to upload</span>
              <input
                ref="importFileField"
                type="file"
                style="display: none"
                @change="uploadParticipants"
              >
            </label>
          </div>
          <div class="upload-col description">
            <div>Participants can be uploaded from an XLSX (Excel) spreadsheet file</div>
          </div>
        </div>
        <div
          v-if="downloadTemplateButtonVisible"
          class="upload-row"
        >
          <div class="upload-col tight button">
            <a
              :href="templateUrl"
              class="btn btn-sm btn-secondary"
            >
              <span class="glyphicons icon-left download align-middle" /><span class="align-middle">Download a template</span>
            </a>
          </div>
          <div class="upload-col description">
            <div>Use this XLSX template to enter and upload participants</div>
          </div>
        </div>
      </div>
      <progress-indicator
        v-if="isUploading"
        :progress="uploadProgress"
        :message="progressMessage"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import {computed, ref, useTemplateRef} from 'vue'

import Modal from './modal.vue'
import ProgressIndicator from './progress-indicator.vue'

import {isNonEmptyString} from '../js-utils'

interface UploadResponse {
  url?: string
  error?: string
}

const props = defineProps({
  uploadUrl: {default: null, type: String},
  templateUrl: {default: null, type: String},
})

const modal = useTemplateRef<InstanceType<typeof Modal>>('modal')
const importFileField = useTemplateRef<HTMLInputElement>('importFileField')

const isUploading = ref(false)
const progressMessage = ref<string>()
const uploadProgress = ref(0)

const welcomeVisible = computed((): boolean => !isUploading.value)
const uploadButtonVisible = computed((): boolean => isNonEmptyString(props.uploadUrl))
const downloadTemplateButtonVisible = computed((): boolean => isNonEmptyString(props.templateUrl))

const showModal = (): void => {
  modal.value?.showModal()
}

const uploadParticipants = (): void => {
  const uploadField = importFileField.value

  if (!uploadField || uploadField.files == null || !isNonEmptyString(props.uploadUrl)) {
    return
  }

  const file = uploadField.files[0]
  if (file == null) {
    // IE 11 will fire change event when setting uploadField.value to ''
    // if file is null, then do not anything
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  // IE 11 will fire change event again
  uploadField.value = ''

  isUploading.value = true
  progressMessage.value = 'Uploading file'
  uploadProgress.value = 0
  modal.value?.performRequest<UploadResponse>(props.uploadUrl, formData, {
    withLoadingIndicator: false,
    progressCallback: (progress: number, completed: boolean) => {
      uploadProgress.value = progress
      if (completed) {
        progressMessage.value = 'Processing file'
      }
    }
  },
  (status, data) => {
    isUploading.value = false
    if (status) {
      if (isNonEmptyString(data?.url)) {
        window.location.href = data.url
      } else if (isNonEmptyString(data?.error)) {
        modal.value?.showError(data.error)
      } else {
        modal.value?.showError('An error occurred')
      }
    }
  })
}

defineExpose({
  showModal,
})
</script>
